import React, { useEffect, useRef } from "react";
import { Outlet } from "react-router-dom";
import { usePPTActions, usePPTState } from "tap/store/ppt/usePPT";
import { usePPTToasts } from "tap/pages/PPT/hooks/usePPTToasts";
import { GRID_PREFERENCES_STATUS } from "ppt/constants";

/**
 * Layout route for every PPM page: kicks off the one per-session fetch of the
 * user's grid preferences and renders the page underneath it.
 *
 * Preferences are a session-wide concern, not a page-level one — the saved
 * column layouts are keyed by grid, and the same grids appear across pages. So
 * the fetch belongs above the pages, dispatched once, rather than in each page's
 * mount effect. Sitting on a pathless parent route means it stays mounted across
 * navigation within /ppt, and any PPM page added inside it inherits the fetch.
 *
 * Deliberately non-blocking: it renders the Outlet immediately so page chrome
 * appears at once.
 *
 * The thunk already no-ops unless the status is NOT_REQUESTED, so a remount, a
 * StrictMode double-effect or a second PPM page can't trigger a second request.
 */
export const PPMPreferencesGate = () => {
  const { gridPreferencesStatus } = usePPTState();
  const { loadGridPreferencesOnce } = usePPTActions();
  const { showErrorToast } = usePPTToasts();

  // Mount-scoped rather than effect-scoped, because the effect below tears
  // itself down: its first act is to dispatch, which moves the status it depends
  // on. A `cancelled` flag local to the effect run would therefore be set before
  // the fetch ever resolved, silently swallowing the error toast.
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  useEffect(() => {
    if (gridPreferencesStatus !== GRID_PREFERENCES_STATUS.NOT_REQUESTED) return;

    loadGridPreferencesOnce().then((result) => {
      // A failure isn't fatal — the store has already settled on empty
      // preferences so the grids render with their default columns. The toast is
      // there so a user whose saved layout silently didn't come back knows why,
      // rather than assuming their customizations were thrown away. Suppressed
      // once we're unmounted: the user has left PPM and a toast about grid
      // layouts would have no context to land in.
      if (isMountedRef.current && result?.status === "failed") {
        showErrorToast(
          "Couldn't load your saved grid layouts. Showing default columns.",
        );
      }
    });
  }, [gridPreferencesStatus, loadGridPreferencesOnce, showErrorToast]);

  return <Outlet />;
};
